import React, { useState, useEffect } from 'react';
import { Users, Clock, Send, Heart, Award, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { mockData } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Volunteer = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    availability: '',
    message: ''
  });

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/volunteer-opportunities`);
      setOpportunities(response.data);
    } catch (error) {
      // Fallback to mock data
      setOpportunities(mockData.volunteerOpportunities);
    } finally {
      setIsLoadingOpportunities(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.interest) {
      toast.error("Please select an area of interest.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert single interest to array format for backend compatibility
      const submissionData = {
        ...formData,
        interests: [formData.interest]
      };
      await axios.post(`${BACKEND_URL}/api/submissions/volunteer`, submissionData);
      setSubmitted(true);
      toast.success("Application submitted successfully! We'll contact you soon.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        interest: '',
        availability: '',
        message: ''
      });
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="volunteer-page">
      {/* Hero Section - Updated to match site colors */}
      <section className="bg-gradient-to-br from-[#E8E2D0] via-[#F5F2E8] to-[#D1C3A2] py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#0A345E] mb-6">Volunteer With Us</h1>
          <p className="text-xl text-gray-700 leading-relaxed">
            Join our community of dedicated volunteers making a lasting impact.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed mt-2">
            Your time and talents can transform lives.
          </p>
        </div>
      </section>

      {/* Why Volunteer - Updated colors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A345E] mb-4">Why Volunteer?</h2>
            <p className="text-lg text-gray-600">Make a difference while growing personally</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl text-center text-[#0A345E]">Build Community</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">Connect with like-minded individuals and build lasting friendships while serving together.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl text-center text-[#0A345E]">Develop Skills</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">Gain valuable experience and develop new skills that can benefit your personal and professional life.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl text-center text-[#0A345E]">Transform Lives</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">See firsthand how your service creates hope and opportunity for those in need.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Volunteer Opportunities - Updated colors */}
      <section className="py-16 bg-[#F5F2E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A345E] mb-4">Volunteer Opportunities</h2>
            <p className="text-lg text-gray-600">Find the perfect way to serve</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {isLoadingOpportunities ? (
              <div className="col-span-3 text-center py-8 text-gray-500">Loading opportunities...</div>
            ) : opportunities.length === 0 ? (
              <div className="col-span-3 text-center py-8 text-gray-500">No volunteer opportunities available at this time.</div>
            ) : (
              opportunities.map((opportunity) => (
              <Card key={opportunity.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-white" data-testid={`opportunity-${opportunity.id}`}>
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-[#007A7A] transition-colors duration-200 text-[#0A345E]">
                    {opportunity.title}
                  </CardTitle>
                  <CardDescription className="flex items-center text-gray-600">
                    <Clock className="w-4 h-4 mr-2" />
                    {opportunity.time}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{opportunity.description}</p>
                  {opportunity.note && (
                    <p className="text-sm text-amber-700 mt-3 italic">
                      Note: {opportunity.note}
                    </p>
                  )}
                </CardContent>
              </Card>
            )))}
          </div>
        </div>
      </section>

      {/* Application Form - Updated colors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0A345E] mb-4">Make An Impact</h2>
              <p className="text-lg text-gray-600">Fill out the form below and we&apos;ll be in touch soon</p>
            </div>

            <Card className="shadow-2xl border-0">
              <CardContent className="p-8">
                {submitted ? (
                  <div className="text-center py-12" data-testid="volunteer-success-message">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A345E] mb-3">Application Submitted!</h3>
                    <p className="text-gray-600 mb-6">Thank you for your interest in volunteering. We'll contact you soon to discuss next steps.</p>
                    <Button
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="border-[#007A7A] text-[#007A7A]"
                    >
                      Submit Another Application
                    </Button>
                  </div>
                ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="volunteer-form">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                      placeholder="John Doe"
                      data-testid="volunteer-name-input"
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                        placeholder="john@example.com"
                        data-testid="volunteer-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                        placeholder="(555) 123-4567"
                        data-testid="volunteer-phone-input"
                      />
                    </div>
                  </div>

                  {/* Areas of Interest - Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Area of Interest *</label>
                    <select
                      name="interest"
                      required
                      value={formData.interest}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                      data-testid="volunteer-interest-select"
                    >
                      <option value="">Select an area of interest</option>
                      {opportunities.map((opp) => (
                        <option key={opp.id} value={opp.title}>{opp.title}</option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Availability */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Availability *</label>
                    <select
                      name="availability"
                      required
                      value={formData.availability}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                      data-testid="volunteer-availability-select"
                    >
                      <option value="">Select your availability</option>
                      <option value="Weekdays">Weekdays</option>
                      <option value="Weekends">Weekends</option>
                      <option value="Both Weekdays & Weekends">Both Weekdays & Weekends</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tell Us About Yourself</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent transition-all duration-200"
                      placeholder="Share your interests, skills, and why you want to volunteer..."
                      data-testid="volunteer-message-input"
                    />
                  </div>

                  {/* Submit Button - Updated colors */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#0A345E] to-[#007A7A] hover:from-[#0A345E]/90 hover:to-[#007A7A]/90 text-white py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                    data-testid="volunteer-submit-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Volunteer;
